import { Button } from "@astryxdesign/core/Button";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { DateInput } from "@astryxdesign/core/DateInput";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Icon } from "@astryxdesign/core/Icon";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Layout, LayoutContent, LayoutFooter } from "@astryxdesign/core/Layout";
import { Markdown } from "@astryxdesign/core/Markdown";
import { Item } from "@astryxdesign/core/Item";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@astryxdesign/core/SegmentedControl";
import { HStack, StackItem, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TextArea } from "@astryxdesign/core/TextArea";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Thumbnail } from "@astryxdesign/core/Thumbnail";
import { Timestamp } from "@astryxdesign/core/Timestamp";
import { useToast } from "@astryxdesign/core/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import type { ChecklistEntry, Idea, LinkPreview } from "../../shared/types";
import { CATEGORIES } from "../../shared/types";
import { api } from "../lib/api";
import { CATEGORY_META, domainOf, faviconUrl, normalizeUrl, type IsoDate } from "../lib/format";
import { submitOnEnter } from "../lib/forms";
import {
  CheckIcon,
  ExternalIcon,
  GlobeIcon,
  PinIcon,
  PlusIcon,
  SparkleIcon,
  TrashIcon,
} from "../lib/icons";
import { ideasKey, useDeleteWithUndo, usePatchIdea } from "../lib/queries";

interface Props {
  idea: Idea;
  onClose: () => void;
}

/** Mounted with key={idea.id}, so local text state resets per idea. */
export function IdeaDialog({ idea, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const patchIdea = usePatchIdea();
  const deleteWithUndo = useDeleteWithUndo();

  const [title, setTitle] = useState(idea.title);
  const [notes, setNotes] = useState(idea.notes);
  const [newLink, setNewLink] = useState("");
  const [newStep, setNewStep] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const patch = (p: Parameters<typeof patchIdea.mutate>[0]["patch"]) =>
    patchIdea.mutate({ id: idea.id, patch: p });

  const commitText = () => {
    const t = title.trim();
    if (t && t !== idea.title) patch({ title: t });
    if (notes !== idea.notes) patch({ notes });
  };

  const close = () => {
    commitText();
    onClose();
  };

  /** Latest links/checklist from cache — async meta fetches outlive this render. */
  const current = (): Idea =>
    qc.getQueryData<Idea[]>(ideasKey)?.find((i) => i.id === idea.id) ?? idea;

  function addLink(e?: FormEvent) {
    e?.preventDefault();
    const url = normalizeUrl(newLink);
    if (!url) return;
    setNewLink("");
    const placeholder: LinkPreview = { url, domain: domainOf(url) };
    patch({ links: [...current().links, placeholder] });
    void api
      .linkMeta(url)
      .then((meta) => {
        patch({ links: current().links.map((l) => (l.url === url ? meta : l)) });
      })
      .catch(() => {});
  }

  const removeLink = (url: string) => patch({ links: current().links.filter((l) => l.url !== url) });

  function addStep(e?: FormEvent) {
    e?.preventDefault();
    const text = newStep.trim();
    if (!text) return;
    setNewStep("");
    const entry: ChecklistEntry = { id: crypto.randomUUID(), text, done: false };
    patch({ checklist: [...current().checklist, entry] });
  }

  const toggleStep = (id: string, done: boolean) =>
    patch({ checklist: current().checklist.map((s) => (s.id === id ? { ...s, done } : s)) });

  const removeStep = (id: string) =>
    patch({ checklist: current().checklist.filter((s) => s.id !== id) });

  function acceptSuggestion(text: string) {
    const now = current();
    const remaining = (now.suggestions?.checklist ?? []).filter((s) => s !== text);
    patch({
      checklist: [...now.checklist, { id: crypto.randomUUID(), text, done: false }],
      suggestions: remaining.length > 0 ? { checklist: remaining } : null,
    });
  }

  function toggleDone() {
    const nowDone = idea.status !== "done";
    commitText();
    patch({ status: nowDone ? "done" : "open" });
    if (nowDone) {
      toast({ body: "Done ✓", uniqueID: "idea-done" });
      onClose();
    }
  }

  function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(() => setConfirmDelete(false), 2500);
      return;
    }
    deleteWithUndo(current());
    onClose();
  }

  const done = idea.status === "done";
  const checkedCount = idea.checklist.filter((s) => s.done).length;

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => !open && close()}
      width={560}
      // Sized against the SAFE viewport: a centered dialog on an installed
      // iOS PWA (viewport-fit=cover) otherwise puts its header under the
      // status bar and the close/pin buttons become unreachable.
      maxHeight="calc(100dvh - 2 * max(env(safe-area-inset-top, 0px), env(safe-area-inset-bottom, 0px)) - 32px)"
      purpose="form"
    >
      <Layout
        header={
          <DialogHeader
            title="Idea"
            onOpenChange={(open) => !open && close()}
            endContent={
          <HStack gap={0.5}>
            <IconButton
              label="Research with AI"
              tooltip="Research: prices, alternatives, verdict"
              size="sm"
              variant="ghost"
              icon={<GlobeIcon />}
              clickAction={async () => {
                try {
                  const updated = await api.research(idea.id);
                  qc.setQueryData<Idea[]>(ideasKey, (list) =>
                    (list ?? []).map((i) => (i.id === updated.id ? updated : i)),
                  );
                  toast({ body: "🔎 Research ready", uniqueID: "research" });
                } catch {
                  toast({ body: "Research failed", type: "error", uniqueID: "research" });
                }
              }}
            />
            <IconButton
              label={idea.pinned ? "Unpin" : "Pin to top"}
              tooltip={idea.pinned ? "Unpin" : "Pin to top"}
              size="sm"
              variant={idea.pinned ? "primary" : "ghost"}
              icon={<PinIcon />}
              onClick={() => patch({ pinned: !idea.pinned })}
            />
          </HStack>
            }
          />
        }
        content={
          <LayoutContent>
            <VStack gap={4}>
        <TextInput
          label="Title"
          isLabelHidden
          size="lg"
          value={title}
          onChange={setTitle}
          onBlur={commitText}
          placeholder="What is it?"
        />
        <HStack gap={2} wrap="wrap" align="center">
          <SegmentedControl
            label="Category"
            size="sm"
            value={idea.category}
            onChange={(v) => patch({ category: v as Idea["category"] })}
          >
            {CATEGORIES.map((c) => (
              <SegmentedControlItem key={c} value={c} label={CATEGORY_META[c].label} />
            ))}
          </SegmentedControl>
          <StackItem size="fill">
            <DateInput
              label="Due"
              isLabelHidden
              size="sm"
              placeholder="Due date"
              hasClear
              format="date"
              {...(idea.due ? { value: idea.due as IsoDate } : {})}
              onChange={(v) => patch({ due: v ?? null })}
            />
          </StackItem>
        </HStack>
        <TextArea
          label="Notes"
          isLabelHidden
          placeholder="Notes, context, sizes, prices…"
          rows={3}
          value={notes}
          onChange={setNotes}
          onBlur={commitText}
        />

        <VStack gap={1.5}>
          <Text type="label" color="secondary">
            Links
          </Text>
          {idea.links.map((link) => (
            <Item
              key={link.url}
              density="compact"
              startContent={
                link.image ? (
                  <Thumbnail src={link.image} alt="" label={link.domain} />
                ) : (
                  <img
                    src={faviconUrl(link.domain)}
                    alt=""
                    width={20}
                    height={20}
                    style={{ borderRadius: 4, display: "block" }}
                  />
                )
              }
              label={link.title ?? link.domain}
              labelLines={1}
              description={
                <Text type="supporting" size="xsm" maxLines={1}>
                  {link.url}
                </Text>
              }
              endContent={
                <HStack gap={0.5}>
                  <IconButton
                    label="Open link"
                    tooltip={`Open ${link.domain}`}
                    size="sm"
                    variant="ghost"
                    icon={<ExternalIcon />}
                    onClick={() => window.open(link.url, "_blank", "noopener,noreferrer")}
                  />
                  <IconButton
                    label="Remove link"
                    size="sm"
                    variant="ghost"
                    icon={<TrashIcon />}
                    onClick={() => removeLink(link.url)}
                  />
                </HStack>
              }
            />
          ))}
          <form onSubmit={addLink} onKeyDown={submitOnEnter(() => addLink())}>
            <TextInput
              label="Add link"
              isLabelHidden
              size="sm"
              placeholder="Paste a link and press Enter"
              value={newLink}
              onChange={setNewLink}
            />
          </form>
        </VStack>

        <VStack gap={1.5}>
          <Text type="label" color="secondary">
            Checklist{idea.checklist.length > 0 ? ` · ${checkedCount}/${idea.checklist.length}` : ""}
          </Text>
          {idea.checklist.map((step) => (
            <HStack key={step.id} gap={1} align="center">
              <StackItem size="fill">
                <CheckboxInput
                  label={step.text}
                  size="sm"
                  value={step.done}
                  onChange={(checked) => toggleStep(step.id, checked)}
                />
              </StackItem>
              <IconButton
                label="Remove step"
                size="sm"
                variant="ghost"
                icon={<TrashIcon />}
                onClick={() => removeStep(step.id)}
              />
            </HStack>
          ))}
          <form onSubmit={addStep} onKeyDown={submitOnEnter(() => addStep())}>
            <TextInput
              label="Add step"
              isLabelHidden
              size="sm"
              placeholder="Add a step and press Enter"
              value={newStep}
              onChange={setNewStep}
            />
          </form>
        </VStack>

        {idea.research ? (
          <VStack gap={1.5}>
            <HStack gap={1} align="center">
              <Icon icon={GlobeIcon} color="accent" size="sm" />
              <Text type="label" color="secondary">
                Research
              </Text>
              <StackItem size="fill">
                <Timestamp value={new Date(idea.research.at).toISOString()} format="auto" size="xsm" />
              </StackItem>
              <IconButton
                label="Remove research"
                tooltip="Remove research"
                size="sm"
                variant="ghost"
                icon={<TrashIcon />}
                onClick={() => patch({ research: null })}
              />
            </HStack>
            <Markdown>{idea.research.text}</Markdown>
          </VStack>
        ) : null}

        {idea.suggestions && idea.suggestions.checklist.length > 0 ? (
          <VStack gap={1.5}>
            <HStack gap={1} align="center">
              <Icon icon={SparkleIcon} color="accent" size="sm" />
              <StackItem size="fill">
                <Text type="label" color="secondary">
                  Suggested steps
                </Text>
              </StackItem>
              <Button
                label="Dismiss"
                size="sm"
                variant="ghost"
                onClick={() => patch({ suggestions: null })}
              />
            </HStack>
            {idea.suggestions.checklist.map((s) => (
              <HStack key={s} gap={1} align="center">
                <StackItem size="fill">
                  <Text type="body" color="secondary">
                    {s}
                  </Text>
                </StackItem>
                <IconButton
                  label={`Add step: ${s}`}
                  tooltip="Add to checklist"
                  size="sm"
                  variant="ghost"
                  icon={<PlusIcon />}
                  onClick={() => acceptSuggestion(s)}
                />
              </HStack>
            ))}
          </VStack>
        ) : null}

            </VStack>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} align="center">
              <Button
                label={done ? "Reopen" : "Mark done"}
                variant={done ? "secondary" : "primary"}
                icon={<CheckIcon />}
                onClick={toggleDone}
              />
              <StackItem size="fill">
                <Text type="supporting" size="xsm" color="secondary">
                  added{" "}
                  <Timestamp
                    value={new Date(idea.createdAt).toISOString()}
                    format="auto"
                    type="inherit"
                  />
                </Text>
              </StackItem>
              <Button
                label={confirmDelete ? "Really delete?" : "Delete"}
                variant="destructive"
                icon={<TrashIcon />}
                onClick={handleDelete}
              />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
